export const DEFAULT_DEV_OPTIONS = {
  waterColor: "#b8cdf1",
  landColor: "#f7e1c4",
  clusterColor: "#3d6098",
  markerSize: 100,
  clusterSize: 100,
  clusterTransparency: 15,
};

export const HEX_INPUT_PATTERN = /^#?[0-9a-fA-F]{0,6}$/;

export const clampNumber = (value, min, max) =>
  Math.min(max, Math.max(min, Number(value)));

export const normalizeHexColor = (value, fallback) => {
  let hex = String(value ?? "").trim();
  if (!hex) return fallback;
  if (!hex.startsWith("#")) hex = `#${hex}`;

  if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
    const [, r, g, b] = hex;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
    return hex.toLowerCase();
  }

  return fallback;
};

export const shadeHexColor = (color, amount) => {
  const hex = normalizeHexColor(color, DEFAULT_DEV_OPTIONS.clusterColor);
  const number = parseInt(hex.slice(1), 16);
  const adjust = (channel) =>
    Math.max(0, Math.min(255, channel + amount)).toString(16).padStart(2, "0");

  return `#${adjust((number >> 16) & 255)}${adjust((number >> 8) & 255)}${adjust(number & 255)}`;
};

const withFeatureColor = (styles, featureType, color) => {
  let updatedFeature = false;
  const nextStyles = styles.map((style) => {
    if (style.featureType !== featureType) return style;
    updatedFeature = true;
    const stylers = style.stylers ?? [];
    const hasColor = stylers.some((styler) => "color" in styler);
    return {
      ...style,
      stylers: hasColor
        ? stylers.map((styler) => ("color" in styler ? { color } : styler))
        : [{ color }, ...stylers],
    };
  });

  return updatedFeature
    ? nextStyles
    : [...nextStyles, { featureType, stylers: [{ color }] }];
};

export const buildMapOptions = (baseOptions, devOptions) => {
  const waterColor = normalizeHexColor(
    devOptions.waterColor,
    DEFAULT_DEV_OPTIONS.waterColor,
  );
  const landColor = normalizeHexColor(
    devOptions.landColor,
    DEFAULT_DEV_OPTIONS.landColor,
  );
  const baseStyles = baseOptions.styles ?? [];
  const stylesWithWater = withFeatureColor(baseStyles, "water", waterColor);

  return {
    ...baseOptions,
    styles: withFeatureColor(stylesWithWater, "landscape", landColor),
  };
};

export const getSafeDevOptions = (options) => ({
  waterColor: normalizeHexColor(
    options.waterColor,
    DEFAULT_DEV_OPTIONS.waterColor,
  ),
  landColor: normalizeHexColor(options.landColor, DEFAULT_DEV_OPTIONS.landColor),
  clusterColor: normalizeHexColor(
    options.clusterColor,
    DEFAULT_DEV_OPTIONS.clusterColor,
  ),
  markerSize: clampNumber(options.markerSize, 50, 200),
  clusterSize: clampNumber(options.clusterSize, 50, 200),
  clusterTransparency: clampNumber(options.clusterTransparency, 0, 100),
});
